import { RecurringTransactionOptions } from '../../Utils/api/DropdownfilterApi';
import React, { useEffect, useState } from 'react';
import { Alert, Button, Col, Layout, Row, Select, Space, Table } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Navbar from '../../Components/Navbar/Navbar';
import { UseGetRequest, UsePostRequest } from '../../Utils/api/ApiRequests';
import { formatAmount, formatDate } from '../../Utils/Common';
import '../../CssFiles/Generatebillings.css';

const GenerateBillings = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [dataSource, setDataSource] = useState({ data: [], size: 0 });
    const [filterConfig, setFilterConfig] = useState({ member_id: null, page: 1, data_size: 10 });
    const [fetching, setFetching] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [warnings, setWarnings] = useState([]);
    const [fetchError, setFetchError] = useState('');
    const [generationResult, setGenerationResult] = useState(null);
    const billingConfig = location.state?.billingConfig;

    useEffect(() => {
        if (!billingConfig) {
            navigate('/accounting/billing', { replace: true });
            return;
        }
        let isActive = true;

        // RULES: Only unbilled members appear in this review; preview does not create records.
        async function fetchPreview() {
            setFetching(true);
            const response = await UseGetRequest('billings/preview', billingConfig);
            if (!isActive) return;
            if (response.data) {
                const rows = (response.data.billings || []).filter((member) => member.can_generate);
                setDataSource({ data: rows, size: rows.length });
                setWarnings(Object.values(response.data.warnings || {}));
            } else {
                setFetchError('Unable to load billing preview. Go back and try again.');
            }
            setFetching(false);
        }

        fetchPreview();
        return () => { isActive = false; };
    }, [billingConfig, navigate]);

    const visibleMembers = dataSource.data.filter((member) =>
        filterConfig.member_id == null || member.id === filterConfig.member_id
    );
    const eligibleMembers = visibleMembers.filter((member) => member.can_generate);
    const recurringLabels = {
        ...Object.fromEntries(RecurringTransactionOptions().map((option) => [option.value, option.label])),
        monthly_due: 'Monthly Due',
        golf_cart: 'Golf Cart',
    };
    const recurringTypes = [...new Set([
        ...Object.keys(recurringLabels),
        ...dataSource.data.flatMap((member) => Object.keys(member.charges || {})),
    ])].filter((chargeType) => chargeType !== 'balance_forwarded');
    
    const transactionColumns = [
        { key: 'proshop', title: 'Proshop Purchases' },
        { key: 'fnb', title: 'Food & Beverage (F&B)' },
        { key: 'sports', title: 'Sports Transactions' },
        { key: 'tournament', title: 'Tournament Registration' },
        { key: 'other', title: 'Other Chargeable Transactions' },
    ];

    // Recurring types get individual columns; other categories display their amounts directly.
    const renderAmount = (amount) => formatAmount(amount || 0);
    const columns = [
        { 
            title: 'Members', 
            dataIndex: 'member_name', 
            key: 'member_name', 
            width: 260, 
            fixed: 'left' 
        },
        { 
            title: 'SA #', 
            dataIndex: 'sa_number', 
            key: 'sa_number', 
            width: 140,
            render: (saNumber) => saNumber || 'Pending' },
        {
            title: 'Recurring Transactions',
            onHeaderCell: () => ({ className: 'billing-category-divider' }),
            children: recurringTypes.map((chargeType, index) => ({
                title: recurringLabels[chargeType] || chargeType.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase()),
                key: chargeType, width: 190, align: 'right',
                onHeaderCell: () => ({ className: 'billing-recurring-type-header' }),
                className: index === recurringTypes.length - 1 ? 'billing-category-divider' : '',
                render: (_, member) => renderAmount(member.charges?.[chargeType]),
            })),
        },
        ...transactionColumns.map((category) => ({
            title: category.title, 
            key: category.key, 
            width: 220, 
            align: 'right',
            className: 'billing-category-divider',
            // No amount is shown until the API provides the category's actual transaction total.
            render: (_, member) => member.transaction_totals?.[category.key] == null
                ? '—' : renderAmount(member.transaction_totals[category.key]),
        })),
        {  
            title: 'Balance Forwarded', 
            key: 'balance_forwarded', 
            width: 190, 
            align: 'right',
            render: (_, member) => renderAmount(member.charges?.balance_forwarded) 
        },
        { 
            title: 'Total', 
            dataIndex: 'grand_total', 
            key: 'grand_total', 
            width: 200, 
            align: 'right',
            render: renderAmount 
        },
    ];

    // Generate only eligible members matching the current member filter, across all table pages.
    const handleSubmit = async () => {
        if (submitting || !eligibleMembers.length) return;
        setSubmitting(true);
        try {
            const response = await UsePostRequest('billings/generate', {
                ...billingConfig,
                member_ids: eligibleMembers.map((member) => member.id),
            });
            // This POST helper exposes unwrapped endpoint payloads through response.
            const result = response.data || response.response;
            if (!result) {
                toast.error('Unable to generate billings. Please try again.');
                return;
            }
            if (result.success_count > 0) {
                toast.success(`${result.success_count} billing(s) generated.`);
            }
            if (result.error_count > 0 || !result.success_count) {
                setGenerationResult(result);
                // Refresh eligibility after partial success to prevent resubmitting completed members.
                const preview = await UseGetRequest('billings/preview', billingConfig);
                if (preview.data) {
                    const rows = (preview.data.billings || []).filter((member) => member.can_generate);
                    setDataSource({ data: rows, size: rows.length });
                } else {
                    setFetchError('Unable to refresh billing status. Go back before generating again.');
                }
            } else {
                navigate('/accounting/billing');
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Layout className='page'>
            <Navbar navKey='credit_billing' />
            <Layout className='content-container overflow-y'>
                <Row className='custom-padding'>
                    <Col span={24}>
                        <Button type='text' icon={<ArrowLeftOutlined />} disabled={submitting}
                            onClick={() => navigate('/accounting/billing')}>Back</Button>
                        <div className='header-container'>Generate Billings</div>
                    </Col>
                </Row>
                <div className='page-content'>
                    <Space direction='vertical' size='large' className='generate-billings-panel'>
                        <Space wrap>
                            <Select className='generate-billings-member-filter' placeholder='All Members'
                                aria-label='Member Name' showSearch allowClear optionFilterProp='label'
                                disabled={fetching || submitting} value={filterConfig.member_id}
                                onChange={(memberId) => setFilterConfig((previous) => ({ ...previous, member_id: memberId ?? null, page: 1 }))}
                                options={dataSource.data.map((member) => ({ value: member.id, label: member.member_name }))} />
                            {billingConfig && <span>
                                Period Covered: {formatDate(billingConfig.period_covered)} · Posting Date: {formatDate(billingConfig.posting_date)} · Billing Date: {formatDate(billingConfig.billing_date)}
                            </span>}
                        </Space>
                        {fetchError && <Alert type='error' showIcon message={fetchError} />}
                        {warnings.length > 0 && <Alert type='warning' showIcon message={warnings.join(' ')} />}
                        {generationResult && <Alert type='warning' showIcon message={generationResult.message}
                            description={Object.values(generationResult.errors || {}).join(' ')} />}
                        <Table rowKey='id' bordered columns={columns} dataSource={visibleMembers}
                            loading={fetching} locale={{ emptyText: 'No members awaiting billing for the selected month and member filter.' }} scroll={{ x: Math.max(1400, 2090 + recurringTypes.length * 190) }}
                            pagination={{ current: filterConfig.page, pageSize: filterConfig.data_size,
                                onChange: (page, pageSize) => setFilterConfig((previous) => ({ ...previous, page, data_size: pageSize })) }} />
                        <Row justify='space-between'>
                            <Button disabled={submitting} onClick={() => navigate('/accounting/billing')}>Cancel</Button>
                            <Button type='primary' className='button-color' loading={submitting}
                                disabled={fetching || !!fetchError || !eligibleMembers.length} onClick={handleSubmit}>
                                Submit
                            </Button>
                        </Row>
                    </Space>
                </div>
            </Layout>
        </Layout>
    );
};

export default GenerateBillings;
